/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { getUserPermissionGroupIds } from '../selectors/initiative';
import {
  AppIds,
  CompanyTrackerIds,
  DelegationWithouInitiativeIds,
  hasGroupAccess,
  PERMISSION_GROUPS,
  PortfolioTrackerIds
} from '../utils/permission-groups';
import { useAppSelector } from '../reducers';
import { generateUrl } from '../routes/util';
import { ROUTES } from '../constants/routes';
import {
  AppRootMatcher,
  companyTrackerRouteMatcher,
  materialityTrackerRouteMatcher,
  navigateToRootApp,
  portfolioTrackerRouteMatcher,
} from '../routes/appRootMatcher';
import { emissionsTrackerRouteMatcher } from '../apps/emissions-tracker/routeMatcher';
import { useHistory } from 'react-router';
import { useOktaAuth } from '@okta/okta-react';
import { getDefaultConfig } from '../config/app-config';
import { DisambiguationModalProps } from '../routes/home/partials/DisambiguationModal';
import { ReferralInfoType } from '../types/onboarding';
import { ClickHandlers } from '../routes/home/flavours/types';
import config from '../config';
import { RootInitiativeData } from '@g17eco/types/initiative';
import { getCurrentUser } from '@selectors/user';
import { SettingStorage } from '@services/SettingStorage';
import { SubscriptionService } from '@services/SubscriptionService';
import { ProductCodes } from '../types/products';

interface UseAppClickHandlersProps {
  toggleRequestDemoModal?: () => void;
  shouldOpenInNewTab?: boolean;
  setDisambiguationModal?: (modal: DisambiguationModalProps | undefined) => void;
}

const hasMatchedInitiatives = ({
  appRouteMatcher,
  rootInitiatives,
}: {
  appRouteMatcher: AppRootMatcher;
  rootInitiatives: RootInitiativeData[];
}) => {
  const appInitiatives = appRouteMatcher.filterInitiatives(rootInitiatives);
  return appInitiatives.length > 0;
};

export const useAppClickHandlers = (props: UseAppClickHandlersProps): ClickHandlers => {
  const { toggleRequestDemoModal, shouldOpenInNewTab } = props

  const { oktaAuth } = useOktaAuth();
  const history = useHistory();

  // @TODO @CTL must be determined at build time?
  const defaultConfig = getDefaultConfig();
  const settings = defaultConfig.branding.ctlApp;

  const userPermissionIds = useAppSelector(getUserPermissionGroupIds);
  const surveyAvailable = useAppSelector((state) => state.surveyAvailable.data);
  const isLoggedIn = useAppSelector((state) => state.currentUser.loaded);
  const { data: rootInitiatives } = useAppSelector((state) => state.rootInitiatives);
  const currentUser = useAppSelector(getCurrentUser);

  const hasDelegationAccess = (app: AppIds, userPermIds: string[] | Readonly<string[]>) => {
    // Have survey and trying to access CT/CTL
    if (surveyAvailable && [AppIds.CompanyTracker, AppIds.CompanyTrackerLight].includes(app)) {
      // but does not have access (must be delegation without initiative)
      return !hasGroupAccess(DelegationWithouInitiativeIds, userPermIds)
    }
    return false;
  }

  const redirect = (link: string, referralInfo?: ReferralInfoType) => {
    if (!shouldOpenInNewTab) {
      return history.push(link, referralInfo);
    }
    window.open(link, '_blank')
  }

  const publicLink = (link: string) => redirect(link);
  const showLoginModal = (link: string) => oktaAuth.signInWithRedirect({ originalUri: link });

  const premiumLink = ({
    link,
    requiredPermissions,
    appId,
    appRouteMatcher
  }: {
    link: string;
    requiredPermissions: string | Readonly<string[]>;
    appId: AppIds;
    appRouteMatcher: AppRootMatcher;
  }) => {
    if (!isLoggedIn) {
      showLoginModal(link);
      return;
    }
    // permissionGroup of MT is CT starter, so we need to ensure MT users don't end up in wrong app 
    if (
      (!hasGroupAccess(userPermissionIds, requiredPermissions) && !hasDelegationAccess(appId, userPermissionIds)) ||
      !hasMatchedInitiatives({ appRouteMatcher, rootInitiatives }) 
    ) {
      toggleRequestDemoModal?.();
      return;
    }
    redirect(navigateToRootApp(rootInitiatives, appId));
  };

  const authRequiredLink = (link: string) => {
    if (!isLoggedIn) {
      showLoginModal(link);
      return;
    }
    redirect(link)
  }

  const GoToCompanyTrackerRegistration = (options?: { onboardingPath?: string; referralInfo?: ReferralInfoType }) => {
    const accessLink = () => premiumLink({
      link: generateUrl(ROUTES.COMPANY_TRACKER, { rootAppPath: settings.rootAppPath }),
      requiredPermissions: PERMISSION_GROUPS.COMPANY_TRACKER_LIGHT,
      appId: AppIds.CompanyTrackerLight,
      appRouteMatcher: companyTrackerRouteMatcher
    });
    if (isLoggedIn) {
      return accessLink();
    }

    const canRegister = settings.canRegister;
    if (canRegister) {
      const link = generateUrl(ROUTES.COMPANY_TRACKER_ONBOARDING, { onboardingPath: options?.onboardingPath ?? settings.rootAppPath });
      return redirect(link, options?.referralInfo);
    }

    toggleRequestDemoModal?.();
  }

  const GoToMaterialityTrackerRegistration = (options?: { onboardingPath?: string; referralInfo?: ReferralInfoType }) => {
    const link = generateUrl(ROUTES.MATERIALITY_TRACKER_ONBOARDING, {
      onboardingPath: options?.onboardingPath ?? 'materiality-tracker',
    });
    return redirect(link, options?.referralInfo);
  }

  const goToMaterialityTracker = () => {
    if (!isLoggedIn) {
      showLoginModal(generateUrl(ROUTES.MATERIALITY_TRACKER));
      return;
    }
    if (!hasMatchedInitiatives({ appRouteMatcher: materialityTrackerRouteMatcher, rootInitiatives })) {
      return window.open(config.brochureURL.materialityTracker, '_blank');
    }
    redirect(generateUrl(ROUTES.MATERIALITY_TRACKER));
  };

  const goToEmissionsCalculator = () => {
    // Logged-out / marketplace visitors see the public calculator comparison page.
    if (!isLoggedIn) {
      return redirect(ROUTES.EMISSIONS_CALCULATORS.path);
    }

    const selectedId = SettingStorage.getItem(companyTrackerRouteMatcher.storageKey);
    const roots = companyTrackerRouteMatcher.filterInitiatives(rootInitiatives);
    const rootInitiative = rootInitiatives.find((o) => o._id === selectedId) ?? roots[0];
    if (!rootInitiative) {
      return redirect(ROUTES.EMISSIONS_CALCULATORS.path);
    }

    // If the initiative has Emissions Tracker, open the app directly.
    if (SubscriptionService.hasActiveOrTrialing(ProductCodes.EmissionTracker, rootInitiative.calculatedSubscriptions)) {
      return redirect(emissionsTrackerRouteMatcher.generateUrl(rootInitiative._id));
    }

    // Otherwise send them to the App Integrations page where they can request or set up providers.
    return redirect(generateUrl(ROUTES.APP_INTEGRATIONS, { initiativeId: rootInitiative._id }));
  };

  const goToEmissionsTrackerAlpha = () => {
    if (!isLoggedIn) {
      showLoginModal('/emissions');
      return;
    }
    const selectedId = SettingStorage.getItem(emissionsTrackerRouteMatcher.storageKey);
    const roots = emissionsTrackerRouteMatcher.filterInitiatives(rootInitiatives);
    const rootInitiative = rootInitiatives.find((o) => o._id === selectedId) ?? roots[0];
    if (rootInitiative) {
      return redirect(emissionsTrackerRouteMatcher.generateUrl(rootInitiative._id));
    }
    // Fallback to emissions base path
    redirect('/emissions');
  };
  const goToAssuranceTracker = () => {
    const link = generateUrl(ROUTES.ASSURANCE)
    if (!isLoggedIn) {
      showLoginModal(link);
      return;
    }
    // AT doesn't have initiative so it does not use permission-groups to check
    if (!currentUser?.organizationId) {
      return window.open(config.brochureURL.assuranceTracker, '_blank');
    }
    return redirect(link);
  };

  return {
    GoToCompanyTrackerPro: () =>
      premiumLink({
        link: generateUrl(ROUTES.COMPANY_TRACKER),
        requiredPermissions: CompanyTrackerIds,
        appId: AppIds.CompanyTracker,
        appRouteMatcher: companyTrackerRouteMatcher,
      }),
    GoToCompanyTrackerLight: () =>
      premiumLink({
        link: generateUrl(ROUTES.COMPANY_TRACKER, { rootAppPath: settings.rootAppPath }),
        requiredPermissions: [PERMISSION_GROUPS.COMPANY_TRACKER_LIGHT, ...CompanyTrackerIds],
        appId: AppIds.CompanyTrackerLight,
        appRouteMatcher: companyTrackerRouteMatcher,
      }),
    GoToCompanyTrackerRegistration,
    GoToCompanyTrackerRegistrationDirect: () => GoToCompanyTrackerRegistration({ onboardingPath: 'company-tracker' }),
    GoToPortfolioTracker: () =>
      premiumLink({
        link: generateUrl(ROUTES.PORTFOLIO_TRACKER),
        requiredPermissions: PortfolioTrackerIds,
        appId: AppIds.PortfolioTracker,
        appRouteMatcher: portfolioTrackerRouteMatcher,
      }),
    GoToAssurance: () => goToAssuranceTracker(),
    GoToSDGAP: () => authRequiredLink(generateUrl(ROUTES.SDGAP)),
    GoToEmissionsCalculator: () => goToEmissionsCalculator(),
    GoToEmissionsTrackerAlpha: () => goToEmissionsTrackerAlpha(),
    GoToMaterialityTracker: () => goToMaterialityTracker(),
    GoToWorldTracker: () => publicLink(generateUrl(ROUTES.WORLD_TRACKER)),
    ShowRequestADemo: () => toggleRequestDemoModal?.(),
    GoToMaterialityTrackerRegistration,
  };
}
